import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSchoolProfile from "../../../../hooks/useSchoolProfile";
import { useUIContext } from "../../../../context/UIContext";
import { useAuthContext } from "../../../../context/AuthContext";
import { uploadImage } from "../../../../api/uploader";
import ImageUploader from "../../../../unicomponents/formComponents/input/ImageUploader";

const DEFAULT_PROFILE = {
  form_school_name: "",
  school_address: "",
  school_contact: "",
  school_email: "",
  school_website: "",
  form_logo_1_url: "",
  form_logo_2_url: "",
  school_name_en: "",
  school_address_en: "",
};

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function buildReadonlyFallbackFromUser(user, schoolid) {
  return {
    schoolid: user?.schoolid || schoolid || "",
    schoolname: user?.schoolname || "",
    schooladmin: user?.schooladmin || user?.fullname || "",
    adminid: user?.adminid || user?.username || "",
    status: Number(user?.status ?? 0),
    usergroup: user?.usergroup || "",
  };
}

function normalizeProfileResponse(response, schoolid, userFallback) {
  if (!response) {
    return {
      readonly: userFallback,
      editable: { ...DEFAULT_PROFILE },
    };
  }

  const source = response.profile || response.data || response;
  const readonly = {
    schoolid: source.schoolid || userFallback.schoolid || schoolid || "",
    schoolname: source.schoolname || userFallback.schoolname || "",
    schooladmin: source.schooladmin || userFallback.schooladmin || "",
    adminid: source.adminid || userFallback.adminid || "",
    status: Number(source.status ?? userFallback.status ?? 0),
    usergroup: source.usergroup || userFallback.usergroup || "",
  };

  const editable = {
    ...DEFAULT_PROFILE,
    form_school_name: source.form_school_name || "",
    school_address: source.school_address || "",
    school_contact: source.school_contact || "",
    school_email: source.school_email || "",
    school_website: source.school_website || "",
    form_logo_1_url: source.form_logo_1_url || "",
    form_logo_2_url: source.form_logo_2_url || "",
    school_name_en: source.school_name_en || "",
    school_address_en: source.school_address_en || "",
  };

  return { readonly, editable };
}

export default function SchoolInformation() {
  const { t } = useTranslation();
  const location = useLocation();
  const schoolId = useMemo(() => location.pathname.split("/")[1], [location]);
  const { handleToastCenterTop, setIsLoadingModal } = useUIContext();
  const { user } = useAuthContext();
  const readonlyFallback = useMemo(
    () => buildReadonlyFallbackFromUser(user, schoolId),
    [user, schoolId],
  );

  const [formData, setFormData] = useState(DEFAULT_PROFILE);
  const [logoFiles, setLogoFiles] = useState({
    form_logo_1_url: null,
    form_logo_2_url: null,
  });
  const [readonlyInfo, setReadonlyInfo] = useState(readonlyFallback);

  const {
    profileQuery: { data: profileData },
    upsertProfileMutation,
  } = useSchoolProfile({
    schoolid: schoolId,
    enableAdminProfile: true,
  });

  useEffect(() => {
    const normalized = normalizeProfileResponse(
      profileData,
      schoolId,
      readonlyFallback,
    );
    setReadonlyInfo(normalized.readonly);
    setFormData(normalized.editable);
  }, [profileData, schoolId, readonlyFallback]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoFile = ({ name, value }) => {
    setLogoFiles((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (formData.school_email && !isValidEmail(formData.school_email)) {
      handleToastCenterTop("학교 이메일 형식이 올바르지 않습니다.");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (user?.role !== "admin") {
      handleToastCenterTop("관리자만 저장할 수 있습니다.");
      return;
    }

    if (!validateForm()) return;

    setIsLoadingModal(true);
    try {
      const nextData = { ...formData };
      const uploadTargets = [
        { key: "form_logo_1_url", prefix: `${schoolId}_school_logo1_` },
        { key: "form_logo_2_url", prefix: `${schoolId}_school_logo2_` },
      ];

      for (const target of uploadTargets) {
        const file = logoFiles[target.key];
        if (!file) continue;

        const uploadedUrl = await uploadImage(
          file,
          `${schoolId}/school-profile`,
          user?.username || "admin",
          target.prefix,
        );
        nextData[target.key] = uploadedUrl;
      }

      const payload = {
        schoolid: schoolId,
        ...nextData,
      };

      upsertProfileMutation.mutate(payload, {
        onSuccess: () => {
          setIsLoadingModal(false);
          handleToastCenterTop("학교 정보가 저장되었습니다.");
          setLogoFiles({
            form_logo_1_url: null,
            form_logo_2_url: null,
          });
          setFormData(nextData);
        },
        onError: (error) => {
          setIsLoadingModal(false);
          console.error(error);
          handleToastCenterTop(`저장 실패: ${error.message}`);
        },
      });
    } catch (error) {
      setIsLoadingModal(false);
      console.error(error);
      handleToastCenterTop("로고 업로드 중 오류가 발생했습니다.");
    }
  };

  return (
    <>
      <div className="row mt-3">
        <div className="col-auto">
          <h3 className="fw-bold">
            {t("admin.extraSettings.schoolInformation") || "학교 정보 관리"}
          </h3>
        </div>
        <div className="col-auto ms-auto">
          <button
            type="button"
            className="btn btn-success"
            onClick={handleSave}
            disabled={user?.role !== "admin"}
          >
            {t("common.labels.doSave") || "저장하기"}
          </button>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          <div className="card">
            <div className="card-header fw-bold">관리자 조회 전용 정보</div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle mb-0">
                  <tbody>
                    <tr>
                      <th className="table-light" style={{ width: "14%" }}>
                        학교 아이디
                      </th>
                      <td style={{ width: "19%" }}>
                        {readonlyInfo.schoolid || schoolId || "-"}
                      </td>
                      <th className="table-light" style={{ width: "14%" }}>
                        학교 이름
                      </th>
                      <td style={{ width: "19%" }}>
                        {readonlyInfo.schoolname || "-"}
                      </td>
                      <th className="table-light" style={{ width: "14%" }}>
                        관리자 이름
                      </th>
                      <td style={{ width: "20%" }}>
                        {readonlyInfo.schooladmin || "-"}
                      </td>
                    </tr>
                    <tr>
                      <th className="table-light">관리자 아이디</th>
                      <td>{readonlyInfo.adminid || "-"}</td>
                      <th className="table-light">현재 상태</th>
                      <td>
                        {Number(readonlyInfo.status) >= 1
                          ? `활성 (${readonlyInfo.status ?? 1})`
                          : `비활성 (${readonlyInfo.status ?? 0})`}
                      </td>
                      <th className="table-light">사용 그룹</th>
                      <td>{readonlyInfo.usergroup || "-"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          <div className="card">
            <div className="card-header fw-bold">관리자 편집 정보</div>
            <div className="card-body">
              <div className="row g-2">
                <div className="col-md-6">
                  <label className="form-label mb-1">양식용 학교이름</label>
                  <input
                    className="form-control"
                    name="form_school_name"
                    value={formData.form_school_name}
                    onChange={handleChange}
                    placeholder="특정 양식에 다르게 표기하고 싶은 학교 이름"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label mb-1">학교 연락처</label>
                  <input
                    className="form-control"
                    name="school_contact"
                    value={formData.school_contact}
                    onChange={handleChange}
                    placeholder="예: 02-123-4567"
                  />
                </div>
                <div className="col-md-12">
                  <label className="form-label mb-1">학교 주소</label>
                  <textarea
                    className="form-control"
                    name="school_address"
                    value={formData.school_address}
                    onChange={handleChange}
                    rows={2}
                    placeholder="학교 주소를 입력해주세요."
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label mb-1">학교 이메일</label>
                  <input
                    className="form-control"
                    name="school_email"
                    value={formData.school_email}
                    onChange={handleChange}
                    placeholder="school@example.com"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label mb-1">학교 웹사이트</label>
                  <input
                    className="form-control"
                    name="school_website"
                    value={formData.school_website}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label mb-1">영문 이름</label>
                  <input
                    className="form-control"
                    name="school_name_en"
                    value={formData.school_name_en}
                    onChange={handleChange}
                    placeholder="English school name"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label mb-1">영문 주소</label>
                  <input
                    className="form-control"
                    name="school_address_en"
                    value={formData.school_address_en}
                    onChange={handleChange}
                    placeholder="English school address"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label mb-1">
                    양식용 학교로고1(기본)
                  </label>
                  <ImageUploader
                    key={`logo1-${formData.form_logo_1_url || "empty"}`}
                    initialImage={formData.form_logo_1_url}
                    onFileSelect={handleLogoFile}
                    name="form_logo_1_url"
                    id="form_logo_1_url"
                    options={[{ width: 280, height: 150 }]}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label mb-1">양식용 학교로고2</label>
                  <ImageUploader
                    key={`logo2-${formData.form_logo_2_url || "empty"}`}
                    initialImage={formData.form_logo_2_url}
                    onFileSelect={handleLogoFile}
                    name="form_logo_2_url"
                    id="form_logo_2_url"
                    options={[{ width: 280, height: 150 }]}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
